import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Settings } from 'design-system/AcSidebar/constants';
import { TabsPanel } from 'design-system/TabPanel/TabPanel';
import { useTabTracking } from 'hooks/useTabTracking';

import PageTopBar from '../Topbar/PageTopBar';

export interface SharedSettingsTab {
  label: string;
  value: string;
}

interface SharedSettingsViewProps {
  tabs: SharedSettingsTab[];
  defaultTab: string;
  headline: string;
  basePath: string;
  onTabChange?: (tab: string) => void;
}

const SharedSettingsView = ({ tabs, defaultTab, headline, basePath, onTabChange }: SharedSettingsViewProps) => {
  const { publisherId, portalId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const currentTab = pathSegments.pop();
    const isCurrentTabAvailable = currentTab && tabs.some(t => t.value === currentTab);

    if (isCurrentTabAvailable) {
      setTab(currentTab);
      return;
    }

    if (tabs.length > 0) {
      const fallbackTab = tabs[0].value;
      setTab(fallbackTab);
      const id = publisherId || portalId;
      navigate(`/${basePath}/${id}/settings/${fallbackTab}`, { replace: true });
    }
  }, [location, tabs, basePath, publisherId, portalId, navigate]);

  const { handleTabChangeWithEvent } = useTabTracking({
    sidebarTab: Settings.title,
    tabs,
    customNavigate: (newValue) => {
      if (onTabChange) {
        onTabChange(newValue);
      }
      setTab(newValue);
      const id = publisherId || portalId;
      const newPath = `/${basePath}/${id}/settings/${newValue}`;
      navigate(newPath);
    }
  });

  return (
    <TabsPanel
      className="settings-view"
      tabs={tabs}
      headerComponent={<PageTopBar withTabsDesign={true} headline={headline} />}
      handleTabChange={handleTabChangeWithEvent}
      activeTabVal={tab}
    />
  );
};

export default SharedSettingsView;
